/* eslint-disable camelcase, no-param-reassign */
import dayjs from 'dayjs';
import pickBy from 'lodash/pickBy';
import storage from 'services/storage.service';
import { REACT_APP_FIREBASE_STORAGE_BASE_URL } from 'config';
import { store, firebase } from '../firebase';
import {
  IFormElementConfig,
  FormFields,
  optionConf,
  FormRenderProps,
  FormStateStruct,
} from '../../types/form.types';
import {
  CriticalPathApprovals,
  CriticalPathStatus,
  CURRENCY_SYMBOLS,
  DEFAULT_CURRENCY,
  TABLE_LIMIT_KEY,
} from '../../constants';
import { formatDate } from './date';
import {
  ICriticalPathTableData,
  DashboardCriticalPathData,
} from '../../types/order.types';
import { IGroundedData } from '../../types/graphql.types';

/**
 * check if a literal is falsy or not
 */
const isLiteralFalsey = (variable: unknown) => {
  return variable === '' || variable === false || variable === 0;
};
/**
 * check if a variable contains a reference type (not a literal)
 */

const isPrimitive = (arg: unknown) => {
  return (
    typeof arg === 'object'
    || (typeof arg !== 'undefined' && arg !== null && typeof arg === 'function')
  );
};
/**
 * check the type name of any primitive and/or reference variable
 */
const checkTypeName = (target: unknown, type: string) => {
  let typeName = '';
  let match = false;
  if (isLiteralFalsey(target) || !isPrimitive(target)) {
    typeName = typeof target;
  } else {
    typeName = Object.prototype.toString.call(target).replace(/^\[object (.+)\]$/, '$1');
  }
  match = Boolean(typeName.toLowerCase().indexOf(type) + 1);
  return match;
};
/**
 * get the actual type of any variable
 *
 * @param {mixed} value
 * @param {String | Array | Function} type
 * @return {Boolean}
 */
export const strictTypeOf = (value: unknown, type: string | Function | string[]) => {
  let result = false;
  /* eslint-disable-next-line no-param-reassign */
  type = type || [];
  if (typeof type === 'object') {
    if (typeof type.length !== 'number') {
      return result;
    }
    let bitPiece = 0;
    /* eslint-disable-next-line no-param-reassign */
    type = [].slice.call(type) as string[];
    type.forEach(($type) => {
      let localResult = false;
      if (typeof $type === 'function') {
        localResult = value instanceof $type;
      }
      /* eslint-disable-next-line no-bitwise */
      bitPiece |= Number(localResult || checkTypeName(value, $type));
    });
    result = Boolean(bitPiece);
  } else {
    if (typeof type === 'function') {
      result = value instanceof type;
    }
    result = result || checkTypeName(value, type as string);
  }
  return result;
};
/**
 * A function that does nothing: returns the first argument only
 *
 * @param {mixed} arg
 * @param {mixed} context
 */
export const noop = (arg: unknown, context?: unknown) => {
  return arg || context;
};
/**
 * A function to group TSX class names for react elements
 *
 * @param {*} styles
 * @return {String}
 */

export const composeClasses = (...styles: (string | boolean | undefined)[]): string => styles.filter((item) => item).join(' ');
/**
 * A function to clean out unecessary props from a react props object
 *
 * @param {Object} props
 * @returns {Object}
 */

export const cleanProps = (props: IFormElementConfig) => {
  const cleaned: { [key: string]: any } = { ...props };
  if (cleaned.field.type === 'combobox') {
    delete cleaned.values;
    delete cleaned.label;
    delete cleaned.value;
    delete cleaned.defaultValue;
  }
  [
    'field',
    'renderType',
    'multiplicity',
    'labelClass',
    'onUpdate',
    'checkValidation',
    'key',
    'cascadeOptions',
  ].forEach((item) => delete cleaned[item]);
  return cleaned;
};
/**
 * A function to extract parent / sub form values only
 *
 * @param {Object} values
 * @returns {Object}
 */

export const extractFormValues = (
  values: { [key: string]: any },
  isParentForm = true
) => {
  return pickBy(values, ($: object, name: string) => {
    const isSubField = /:(?:[\d]{1,})$/.test(name);
    return isParentForm ? !isSubField : isSubField;
  });
};
/**
 * A function that generates random characters
 *
 * @param {Number} length
 * @returns {String}
 */

export const getRandomChars = (length: number = 11): string => {
  const str = (Math.random() + 1).toString(16);
  return str.substr(2, length);
};
/**
 * A function to clean out unecessary state from react state object
 *
 * @param {Object} state
 * @returns {Object}
 */

export const cleanState = (state: FormStateStruct) => {
  const cleaned: { [key: string]: any } = { ...state };
  [
    'subs',
    'subFields',
    'subName',
    'computes',
    'comboboxes',
    'valid',
    'errors',
    'errorElements',
    'hasValidated',
  ].forEach((item) => delete cleaned[item]);
  return cleaned;
};
/**
 * A function that counts words in a given string
 *
 * @param {String} text
 * @return {Number}
 */

export const countWords = (text: string) => {
  return text.split(/\s+/).filter((v) => v).length;
};
/**
 * A function that detects empty object literals/variables
 *
 * @param {Object} obj
 * @return {Boolean}
 */

export const isEmptyObject = (obj: object): boolean => {
  return !strictTypeOf(obj, 'object') || Object.keys(obj).length === 0;
};
/**
 * A function that check all parents for a given id using a sub string
 *
 * @param {HTMLElement} elem
 * @param {String} idNeedle
 * @returns {String|Null}
 */

export const parentsIdFromSubString = (
  elem: HTMLElement | { parentNode: HTMLElement; id: string },
  idNeedle: string
) => {
  let parent = elem
    && (elem.parentNode as HTMLElement | { parentNode: HTMLElement; id: string } | null);
  let maxDepth: number = 5;
  while (parent !== null) {
    if (maxDepth === 0) {
      break;
    }
    if (parent.id.indexOf(idNeedle) > -1) {
      return parent.id;
    }
    parent = parent.parentNode as HTMLElement | { parentNode: HTMLElement; id: string };
    maxDepth -= 1;
  }
  return null;
};
/**
 * A function to format HTML entity symbols using the Unicode values for use in JSX
 *
 * @param {String|Null} textVal
 * @param {String} entityHexVal
 * @param {String} prefix
 *
 * @returns {String}
 */

export const formatEntityValue = (
  textVal: string | null = null,
  entityHexVal: string = '0A0D',
  prefix: string = ''
) => {
  return (
    (textVal ? `${textVal} ` : '')
    + prefix
    + String.fromCharCode(parseInt(entityHexVal, 16))
  );
};
/**
 * A function that retrieves an object descriptor setter
 *
 * @param htmlInterface
 * @param property
 */

export const getDescriptorSetter = (htmlInterface: Function, property: string) => {
  return Object.getOwnPropertyDescriptor(htmlInterface.prototype, property)!.set!;
};
/**
 * A function that programmatically triggers
 */

export const setOptionAndTriggerChange = (
  selectElement: HTMLSelectElement,
  value: string | number
) => {
  const setValue = (combo: HTMLSelectElement, val: string | number) => {
    const { options } = combo;
    const arrOptions: HTMLOptionElement[] = Array.from(options);
    const setIndex = getDescriptorSetter(HTMLSelectElement, 'selectedIndex');
    let selectedIndex = arrOptions.findIndex((option: HTMLOptionElement) => option.value === val) || 0;
    if (selectedIndex < 0 || selectedIndex > arrOptions.length) {
      selectedIndex = 0;
    }
    setIndex.call(combo, selectedIndex);
  };
  setValue(selectElement, value);
  const event = new Event('change', { bubbles: true });
  return selectElement.dispatchEvent(event);
};
/**
 * A function that converts a bas64 string into a Blob (file)
 *
 * @param {String} base64str
 * @param {String} contentType
 * @param {Number} sliceSize
 *
 * @returns {Blob}
 */

export const base64ToBlob = (
  base64str: string,
  contentType: string = '',
  sliceSize: number = 512
) => {
  const byteCharacters = atob(base64str);
  const byteArrays = [];
  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize);
    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }
  const blob = new Blob(byteArrays, { type: contentType });
  return blob;
};
/**
 * A function that maps on object literals
 *
 * @param {Object} obj
 * @param {Function} iterator
 *
 * @returns {Array}
 */

export const objectEach = (obj: { [key: string]: any }, iterator: Function) => {
  const result = [];
  for (const prop in obj) {
    if (Object.hasOwnProperty.call(obj, prop)) {
      result.push(iterator(obj[prop], prop));
    }
  }
  return result;
};
/**
 * A function that loops over arrays with an async callback on eaach iteration
 *
 * @param {Array} list
 * @param {Function} callback
 *
 * @returns {Void}
 */

export const asyncForEach = async (list: any[], callback: Function) => {
  for (let index = 0; index < list.length; index++) {
    /* eslint-disable-next-line no-await-in-loop */
    await callback(list[index], index);
  }
};
/**
 * A function that sets comboboxes dynamically on form field definition objects
 *
 * @param {FormFields} formFields
 * @param {Object} dataContexts
 *
 * @returns {FormFields}
 */
export const setComboboxOptions = (
  formFields: FormFields,
  dataContexts: {
    [key: string]: { data: string[] | optionConf[]; selected?: string };
  } = {}
): FormFields => {
  for (const context in dataContexts) {
    if (context in dataContexts) {
      const formField = formFields[context] as FormRenderProps;
      const dataContext = dataContexts[context];
      formField.options = dataContext.data;
      formField.selected = dataContext.selected;
    }
  }
  return formFields;
};
/**
 * A function that calculates the size of a Blob file
 *
 * @param {Number} bytes
 * @param {Boolean} si
 * @param {Number} dp
 *
 * @returns {String}
 */

export const getFileSizeFromBytes = (
  bytes: number,
  si: boolean = false,
  dp: number = 1
): string => {
  const thresh = si ? 1000 : 1024;
  const factor = 10 ** dp;
  let unitsIndex = -1;
  if (Math.abs(bytes) < thresh) {
    return `${bytes} B`;
  }
  let reducedBytes = bytes;
  const units = si
    ? ['KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB']
    : ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB'];
  do {
    reducedBytes /= thresh;
    ++unitsIndex;
  } while (
    Math.round(Math.abs(reducedBytes) * factor) / factor >= thresh
    && unitsIndex < units.length - 1
    );
  return `${reducedBytes.toFixed(dp)} ${units[unitsIndex]}`;
};
/**
 * A function that schedules a firebase file upload task
 *
 * @param uploadTask
 */

const scheduleUploadTask = (uploadTask: firebase.storage.UploadTask) => {
  return new Promise<string>((resolve, reject) => {
    uploadTask.on(
      firebase.storage.TaskEvent.STATE_CHANGED,
      (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        console.log('cloudstorage upload progress: ', `${progress}%`);
      },
      (error: Error) => {
        console.log(error.message);
        reject(error.message);
      },
      () => {
        uploadTask.snapshot.ref.getDownloadURL().then(resolve);
      }
    );
  });
};
/**
 * A function that uploads images only to a firebase store
 *
 * @param base64str
 * @param type
 * @param size
 * @param name
 */

export const uploadImagesToCloudStorage = (
  base64str: string,
  type: string,
  size: number,
  name: string
) => {
  const uploadTask = store
    .child(`images/${size}_${name}`)
    .put(base64ToBlob(base64str, type), {
      contentType: type,
    });
  return scheduleUploadTask(uploadTask);
};
/**
 * Add a delete function from cloud
 * @param path
 * @param deleteFunction
 */
export const deleteFileFromCloudStorage = (path: string, deleteFunction: Function) => {
  store
    .child(path)
    .delete()
    .then(() => {
      deleteFunction();
    })
    .catch((error) => {
      console.log(error);
    });
};
/**
 * Getting the pathsStorage from url
 * @param url
 */
export const getPathStorageFromUrl = (url: string) => {
  const baseUrl = REACT_APP_FIREBASE_STORAGE_BASE_URL;
  let imagePath: string = url.replace(baseUrl, '');
  const indexOfEndPath = imagePath.indexOf('?');
  imagePath = imagePath.substring(0, indexOfEndPath);
  imagePath = imagePath.replace('%2F', '/');
  return imagePath;
};
/**
 * Delete file from cloud storage using url
 * @param url
 * @param deleteFunction
 */
export const deleteFileFromCloudUsingUrl = async (
  url: string,
  deleteFunction: Function
) => {
  const path = getPathStorageFromUrl(url);
  deleteFileFromCloudStorage(path, deleteFunction);
};
/**
 * A function that uploads pdfs only to a firebase store
 *
 * @param file
 * @param blobUrl
 */

export const uploadPDFsToCloudStorage = (file: Blob, blobUrl: string) => {
  const name = blobUrl.split('/').pop();
  const uploadTask = store.child(`pdfs/${file.size}_${name}`).put(file, {
    contentType: file.type,
  });
  return scheduleUploadTask(uploadTask);
};
/**
 * A function that deletes pdfs from a firebase store
 *
 * @param fileUrl
 */

export const deletePDFsFromCloudStorage = (fileUrl: string) => {
  const file = decodeURIComponent(fileUrl.split('/').pop() as string)
    .split('?')
    .shift();
  return store.child(file as string).delete();
};
/**
 * A function that removes duplicates from an array of objects
 *
 * @param arrayList
 * @param propertyName
 */

export const removeDuplicatesFromArray = (
  arrayList: { [key: string]: unknown }[],
  propertyName: string
) => {
  return arrayList.reduce(
    (acc: { [key: string]: unknown }[], current: { [key: string]: unknown }) => {
      const reduced = acc.find((item) => item[propertyName] === current[propertyName]);
      if (!reduced) {
        return acc.concat([current]);
      }
      return acc;
    },
    []
  );
};
/**
 * A function that removes deletes properties on an object
 *
 * @param payload
 * @param fields
 */

export const removeRedundantObjectProps = (
  payload: { [key: string]: unknown },
  fields: string[]
) => {
  if (!isEmptyObject(payload)) {
    for (const field of fields) {
      const payloadKeys = Object.keys(payload);
      // eslint-disable-next-line no-param-reassign
      if (payloadKeys.includes(field)) delete payload[field];
    }
  }
  return payload;
};
export const parseToSimpleDate = (date: string) => date?.split('T')[0];
export const formatPOStyleNumber = (brandRef1: string, brandRef2: string) => {
  if (!brandRef1 && !brandRef2) return '';
  /* eslint-disable no-unneeded-ternary */
  brandRef1 = brandRef1 ? brandRef1 : '';
  brandRef2 = brandRef2 ? brandRef2 : '';
  if (!brandRef1) return brandRef2;
  if (!brandRef2) return brandRef1;
  return `${brandRef1} / ${brandRef2}`;
};
const getIsVatAppCalcPerc = (
  brandVatFob: number,
  brandVatLanded: number,
  transportMethod: string
) => {
  //  Default set incase a Is Vat Applicable value is not set on the brand
  let isVatAppCalcPerc = 20;
  //  Check whether an Is Vat Applicable value been set on the brand
  if (brandVatFob || brandVatLanded) {
    if (transportMethod === 'fob') {
      isVatAppCalcPerc = brandVatFob;
    } else {
      isVatAppCalcPerc = brandVatLanded;
    }
  }
  return isVatAppCalcPerc;
};
const getDiscountCalcPerc = (
  brandDiscountFob: number,
  brandDiscountLanded: number,
  transportMethod: string
) => (transportMethod === 'fob' ? brandDiscountFob : brandDiscountLanded);
export const normalizeGroundedData = (groundedData: IGroundedData[]) => groundedData.map((it) => {
  const isVatAppCalcPerc = getIsVatAppCalcPerc(
    it.brandVatFob,
    it.brandVatLanded,
    it.transportMethod
  );
  const discountCalcPerc = getDiscountCalcPerc(
    it.brandDiscountFob,
    it.brandDiscountLanded,
    it.transportMethod
  );
  const ocDropDeliveryQuantity = it.deliveredQuantity || 0;
  const ocSalesPrice = it.salesPrice || 0;
  const ocCostPrice = it.costPrice || 0;
  const ocCurrency: string = it.salesPriceCurrency || DEFAULT_CURRENCY;
  const CURRENCY_SYMBOL = CURRENCY_SYMBOLS[ocCurrency];
  const ocTransport = it.transport || 0;
  //  Order of calculation based on the provided spreadsheet
  //  1
  const total = ocSalesPrice * ocDropDeliveryQuantity;
  //  2
  const paymentExVAT = total - total * (discountCalcPerc / 100);
  //  3
  const grandTotal = paymentExVAT * (isVatAppCalcPerc / 100) + total;
  //  4
  const vatTotal = grandTotal - total;
  //  5
  const remittanceAdvise = vatTotal + paymentExVAT;
  //  6
  const discount = total - paymentExVAT;
  const discountByQuantity = discount / ocDropDeliveryQuantity || 0;
  const paymentVatByQuantity = paymentExVAT / ocDropDeliveryQuantity || 0;
  const grossMarkupPrice = ocSalesPrice - ocCostPrice - discountByQuantity - ocTransport;
  const grossMarkupPercent = (grossMarkupPrice / paymentVatByQuantity) * 100;
  return {
    original_agreed_delivery_date: it.agreedDeliveryDate
      ? new Date(it.agreedDeliveryDate)
      : null,
    agreed_delivery_date: formatDate(it.agreedDeliveryDate),
    oc_id: it.ocId,
    pf_style_number: it.styleNumber,
    po_line_number: formatPOStyleNumber(it.brandRef1, it.brandRef2),
    transport: `${CURRENCY_SYMBOL} ${ocTransport.toFixed(2)}`,
    brand: it.brandName,
    brandId: it.brandId,
    department: it.departmentName,
    sales_price: `${CURRENCY_SYMBOL} ${ocSalesPrice.toFixed(2)}`,
    delivered_quantity: ocDropDeliveryQuantity,
    manufacturing_cost: `${CURRENCY_SYMBOL} ${ocCostPrice.toFixed(2)}`,
    total_manufacturing_cost: `${CURRENCY_SYMBOL} ${(
      ocCostPrice * ocDropDeliveryQuantity
    ).toFixed(2)}`,
    gross_markup_price: `${CURRENCY_SYMBOL} ${grossMarkupPrice.toFixed(2)}`,
    gross_markup_percentage: `${
      Number.isFinite(grossMarkupPercent) ? grossMarkupPercent.toFixed(2) : 0
    } %`,
    total: `${CURRENCY_SYMBOL} ${total.toFixed(2)}`,
    vat_total: `${CURRENCY_SYMBOL} ${vatTotal.toFixed(2)}`,
    remittance_advise: `${CURRENCY_SYMBOL} ${remittanceAdvise.toFixed(2)}`,
    payment_ex_vat: `${CURRENCY_SYMBOL} ${paymentExVAT.toFixed(2)}`,
    discount: `${CURRENCY_SYMBOL} ${discount.toFixed(2)}`,
    grand_total: `${CURRENCY_SYMBOL} ${grandTotal.toFixed(2)}`,
    description: it.description,
    booked_quantity: it.totalQuantity,
    factory_1: it.factory1Name,
    factory_2: it.factory2Name,
    color: it.ocDropColor,
    criticalPathId: it.cpId,
    criticalPathStatus: it.cpStatus,
    comment: it.groundedComment || '',
    collection_date: it.collectionDate || '',
    revised_collection_date: it.revisedCollectionDate || '',
    po_sent: it.poSent || '',
    labels_ordered: it.labelsOrdered || '',
    test_report: it.testReport || '',
    invoice_number: it.groundedInvoiceNumber,
    date_of_payment: it.groundedDateOfPayment,
    date_payment_expected: it.groundedDatePaymentExpected,
    /* eslint-disable no-nested-ternary */
    factory_1_paid:
      it.factory1Paid === null || it.factory1Paid === undefined
        ? ''
        : it.factory1Paid
          ? 'YES'
          : 'NO',
    factory_2_paid:
      it.factory2Paid === null || it.factory2Paid === undefined
        ? ''
        : it.factory2Paid
          ? 'YES'
          : 'NO',
    factory_1_invoice_number: it.factory1InvoiceNumber,
    factory_2_invoice_number: it.factory2InvoiceNumber,
    grounded: {
      comment: it.groundedComment || '',
      invoice_number: it.groundedInvoiceNumber,
      date_of_payment: it.groundedDateOfPayment,
      date_payment_expected: it.groundedDatePaymentExpected,
      /* eslint-disable no-nested-ternary */
      factory_1_paid:
        it.factory1Paid === null || it.factory1Paid === undefined
          ? ''
          : it.factory1Paid
            ? 'YES'
            : 'NO',
      factory_2_paid:
        it.factory2Paid === null || it.factory2Paid === undefined
          ? ''
          : it.factory2Paid
            ? 'YES'
            : 'NO',
      factory_1_invoice_number: it.factory1InvoiceNumber,
      factory_2_invoice_number: it.factory2InvoiceNumber,
    },
  };
});
export const normalizeOCDataForGroundedTable = (ocData: any) => {
  let groundedDrops: any[] = [];
  const dropsAdded = new Set();
  ocData.forEach((currentOrderConfirmation: any) => {
    let ocIndex = 0;
    const { brand_ref_number_1, brand_ref_number_2 } = currentOrderConfirmation;
    //  Default set incase a Is Vat Applicable value is not set on the brand
    let isVatAppCalcPerc;
    //  Check whether an Is Vat Applicable value been set on the brand
    if (
      currentOrderConfirmation?.brand?.vatFob !== 0
      || currentOrderConfirmation?.brand?.vatLanded !== 0
    ) {
      if (currentOrderConfirmation?.transport_method === 'fob') {
        isVatAppCalcPerc = currentOrderConfirmation?.brand?.vatFob;
      } else {
        isVatAppCalcPerc = currentOrderConfirmation?.brand?.vatLanded;
      }
    }
    const discountCalcPerc = currentOrderConfirmation?.transport_method === 'fob'
      ? currentOrderConfirmation?.brand?.discountFob
      : currentOrderConfirmation?.brand?.discountLanded;
    while (currentOrderConfirmation.order_confirmation_drop[ocIndex]) {
      const ocDrop = currentOrderConfirmation.order_confirmation_drop[ocIndex];
      const ocDropDeliveryQuantity = ocDrop?.critical_path?.delivered_quantity || 0;
      const ocSalesPrice = currentOrderConfirmation?.sales_price || 0;
      const ocCostPrice = currentOrderConfirmation?.cost_price || 0;
      const ocCurrency: string = currentOrderConfirmation?.sales_price_currency || DEFAULT_CURRENCY;
      const CURRENCY_SYMBOL = CURRENCY_SYMBOLS[ocCurrency];
      const ocTransport = currentOrderConfirmation?.transport;
      //  1
      const dsTotal = ocSalesPrice * ocDropDeliveryQuantity;
      //  2
      const dsPaymentExVAT = dsTotal - dsTotal * (discountCalcPerc / 100);
      //  3
      const dsGrandTotal = dsPaymentExVAT * (isVatAppCalcPerc / 100) + dsTotal;
      //  4
      const dsVatTotal = dsGrandTotal - dsTotal;
      //  5
      const dsRemittanceAdvise = dsVatTotal + dsPaymentExVAT;
      //  6
      const dsDiscount = dsTotal - dsPaymentExVAT;
      const discountByQuantity = dsDiscount / ocDropDeliveryQuantity || 0;
      const paymentVatByQuantity = dsPaymentExVAT / ocDropDeliveryQuantity || 0;
      const dsGrossMarkupPrice = ocSalesPrice - ocCostPrice - discountByQuantity - ocTransport;
      const dsGrossMarkupPercent = (dsGrossMarkupPrice / paymentVatByQuantity) * 100;
      const grounded = ocDrop.critical_path?.grounded;
      //  TODO: Remove all Critical Path-only fields being loaded with the Grounded item data
      const CriticalPathAggregate = {
        id: ocDrop?.id,
        original_agreed_delivery_date: ocDrop?.agreed_delivery_date
          ? new Date(ocDrop?.agreed_delivery_date)
          : null,
        agreed_delivery_date: formatDate(ocDrop?.agreed_delivery_date),
        oc_id: currentOrderConfirmation?.id,
        pf_style_number: currentOrderConfirmation?.pf_style_number,
        po_line_number: formatPOStyleNumber(brand_ref_number_1, brand_ref_number_2),
        transport: `${CURRENCY_SYMBOL} ${ocTransport.toFixed(2)}`,
        brand: currentOrderConfirmation?.brand?.name,
        brandId: currentOrderConfirmation?.brand?.id,
        department: currentOrderConfirmation?.department.name,
        sales_price: `${CURRENCY_SYMBOL} ${ocSalesPrice.toFixed(2)}`,
        delivered_quantity: ocDropDeliveryQuantity,
        manufacturing_cost: `${CURRENCY_SYMBOL} ${ocCostPrice.toFixed(2)}`,
        total_manufacturing_cost: `${CURRENCY_SYMBOL} ${(
          ocCostPrice * ocDropDeliveryQuantity
        ).toFixed(2)}`,
        gross_markup_price: `${CURRENCY_SYMBOL} ${dsGrossMarkupPrice.toFixed(2)}`,
        gross_markup_percentage: `${
          Number.isFinite(dsGrossMarkupPercent) ? dsGrossMarkupPercent.toFixed(2) : 0
        } %`,
        total: `${CURRENCY_SYMBOL} ${dsTotal.toFixed(2)}`,
        vat_total: `${CURRENCY_SYMBOL} ${dsVatTotal.toFixed(2)}`,
        remittance_advise: `${CURRENCY_SYMBOL} ${dsRemittanceAdvise.toFixed(2)}`,
        payment_ex_vat: `${CURRENCY_SYMBOL} ${dsPaymentExVAT.toFixed(2)}`,
        discount: `${CURRENCY_SYMBOL} ${dsDiscount.toFixed(2)}`,
        grand_total: `${CURRENCY_SYMBOL} ${dsGrandTotal.toFixed(2)}`,
        description: ocDrop?.description,
        booked_quantity:
          ocDrop?.blank_stock_order?.total_quantity || ocDrop?.stock_total_quantity || 0,
        factory_1: currentOrderConfirmation?.factory_1?.name,
        factory_2: currentOrderConfirmation?.factory_2?.name,
        color: ocDrop?.color,
        criticalPathId: ocDrop?.critical_path?.id,
        criticalPathStatus: ocDrop?.critical_path?.status?.id,
        comment: ocDrop?.critical_path?.comment || '',
        collection_date: ocDrop?.factory_exit_date || '',
        revised_collection_date: ocDrop?.critical_path?.revised_collection_date || '',
        po_sent: ocDrop?.critical_path?.po_sent || '',
        labels_ordered: ocDrop?.critical_path?.labels_ordered || '',
        test_report: ocDrop?.critical_path?.test_report || '',
      };
      if (ocDrop.critical_path?.status.id === CriticalPathStatus.GROUNDED) {
        /* eslint-disable no-nested-ternary */
        const factory_1_paid = grounded?.factory_1_paid
          ? 'YES'
          : grounded?.factory_1_paid === null
            ? ''
            : 'NO';
        const factory_2_paid = grounded?.factory_2_paid
          ? 'YES'
          : grounded?.factory_2_paid === null
            ? ''
            : 'NO';
        // remove duplicates
        if (!dropsAdded.has(CriticalPathAggregate.id)) {
          dropsAdded.add(CriticalPathAggregate.id);
          groundedDrops = [
            ...groundedDrops,
            {
              ...CriticalPathAggregate,
              grounded,
              comment: grounded?.comment,
              invoice_number: grounded?.invoice_number,
              date_of_payment: grounded?.date_of_payment,
              date_payment_expected: grounded?.date_payment_expected,
              factory_1_paid,
              factory_2_paid,
              factory_1_invoice_number: grounded?.factory_1_invoice_number,
              factory_2_invoice_number: grounded?.factory_2_invoice_number,
            },
          ];
        }
      }
      ocIndex++;
    }
  });
  return groundedDrops;
};
export const normalizeOCDataForCriticalPathTable = (ocData: any) => {
  let criticalPathDrops: any[] = [];
  const dropsAdded = new Set();
  ocData.forEach((currentOrderConfirmation: any) => {
    let ocIndex = 0;
    const { brand_ref_number_1, brand_ref_number_2 } = currentOrderConfirmation;
    while (currentOrderConfirmation.order_confirmation_drop[ocIndex]) {
      const ocDrop = currentOrderConfirmation.order_confirmation_drop[ocIndex];
      const criticalPath = ocDrop?.critical_path;
      const ocDropDeliveryQuantity = criticalPath?.delivered_quantity || 0;
      const CriticalPathAggregate = {
        id: ocDrop?.id,
        brand: currentOrderConfirmation?.brand?.name,
        brandId: currentOrderConfirmation?.brand?.id,
        department: currentOrderConfirmation?.department?.name,
        original_agreed_delivery_date: ocDrop?.agreed_delivery_date
          ? new Date(ocDrop?.agreed_delivery_date)
          : null,
        agreed_delivery_date: formatDate(ocDrop?.agreed_delivery_date),
        oc_id: currentOrderConfirmation?.id,
        pf_style_number: currentOrderConfirmation?.pf_style_number,
        po_line_number: formatPOStyleNumber(brand_ref_number_1, brand_ref_number_2),
        delivered_quantity: ocDropDeliveryQuantity,
        description: ocDrop?.description,
        booked_quantity:
          ocDrop?.blank_stock_order?.total_quantity || ocDrop?.stock_total_quantity || 0,
        factory_1: currentOrderConfirmation?.factory_1?.name,
        factory_2: currentOrderConfirmation?.factory_2?.name,
        color: ocDrop?.color,
        criticalPathId: criticalPath?.id,
        criticalPathStatus: criticalPath?.status?.id,
        comment: criticalPath?.comment || '',
        collection_date: ocDrop?.factory_exit_date || '',
        revised_collection_date: criticalPath?.revised_collection_date || '',
        po_sent: criticalPath?.po_sent || '',
        labels_ordered: criticalPath?.labels_ordered || '',
        test_report: criticalPath?.test_report || '',
        blank_stock_order: ocDrop?.blank_stock_order,
        development_sample_date: '',
        development_sample_r_a: getCriticalPathApprovalStatus(
          criticalPath?.development_sample_r_a
        ),
        production_sample_date: '',
        production_sample_r_a: getCriticalPathApprovalStatus(
          criticalPath?.production_sample_r_a
        ),
      };
      // remove duplicates
      if (!dropsAdded.has(CriticalPathAggregate.id)) {
        dropsAdded.add(CriticalPathAggregate.id);
        criticalPathDrops = [...criticalPathDrops, CriticalPathAggregate];
      }
      ocIndex++;
    }
  });
  return criticalPathDrops;
};
export const normalizeCriticalPathTableData = (cpData: ICriticalPathTableData[]) => {
  const criticalPathDrops: any[] = [];
  const dropsAdded = new Set();
  cpData.forEach((cpRecord: ICriticalPathTableData) => {
    const CriticalPathAggregate = {
      id: cpRecord.ocDropId,
      brand: cpRecord.brandName,
      brandId: cpRecord.brandId,
      department: cpRecord.departmentName,
      blankStockOrderId: cpRecord.bsoId,
      original_agreed_delivery_date: cpRecord.deliveryDate
        ? new Date(cpRecord.deliveryDate)
        : null,
      agreed_delivery_date: formatDate(cpRecord.deliveryDate),
      oc_id: cpRecord.ocId,
      pf_style_number: cpRecord.styleNumber,
      po_line_number: formatPOStyleNumber(
        cpRecord.brandReference1,
        cpRecord.brandReference2
      ),
      delivered_quantity: cpRecord.deliveredQuantity,
      description: cpRecord.description,
      descriptionColor: cpRecord.descriptionColor,
      booked_quantity: cpRecord.bsoTotalQuantity || cpRecord.ocDropTotalQuantity || 0,
      factory_1: cpRecord.factory1Name,
      factory_2: cpRecord.factory2Name,
      color: cpRecord.color,
      criticalPathId: cpRecord.cpId,
      criticalPathStatus: cpRecord.criticalPathStatusId,
      comment: cpRecord.comment || '',
      collection_date: cpRecord.factoryExitDate || '',
      revised_collection_date: cpRecord.revisedCollectionDate || '',
      po_sent: cpRecord.poSent || '',
      labels_ordered: cpRecord.labelsOrdered || '',
      test_report: cpRecord.testReport || '',
      development_sample_date: '',
      development_sample_r_a: getCriticalPathApprovalStatus(
        cpRecord.developmentSampleStatus
      ),
      production_sample_date: '',
      production_sample_r_a: getCriticalPathApprovalStatus(
        cpRecord.productionSampleStatus
      ),
      has_stock_order_quantities: cpRecord.hasStockOrderQuantities,
    };
    if (!dropsAdded.has(CriticalPathAggregate.id)) {
      dropsAdded.add(CriticalPathAggregate.id);
      criticalPathDrops.push(CriticalPathAggregate);
    }
  });
  return criticalPathDrops;
};
export const normalizeDashboardOCDataForCriticalPathTable = (
  ocData: DashboardCriticalPathData[],
  users: []
) => {
  return ocData.map((dashboardCriticalPathData) => ({
    id: dashboardCriticalPathData.ocDropId,
    brand: dashboardCriticalPathData.brandName,
    brand_id: dashboardCriticalPathData.brandId,
    image_thumbnail: dashboardCriticalPathData.imageUrl,
    oc_id: dashboardCriticalPathData.ocId,
    criticalPathId: dashboardCriticalPathData.cpId,
    criticalPathStatus: dashboardCriticalPathData.criticalPathStatusId,
    pf_style_number: dashboardCriticalPathData.styleNumber,
    agreed_delivery_date: dashboardCriticalPathData.deliveryDate
      ? formatDate(new Date(parseInt(dashboardCriticalPathData.deliveryDate, 10)))
      : 'N/A',
    comment: dashboardCriticalPathData.comment || '',
    assigned_user_id: dashboardCriticalPathData.assignedUserId,
    author_id: dashboardCriticalPathData.authorId,
    modified_by: getUserNamesById(dashboardCriticalPathData.modifiedBy, users),
  }));
};
const getUserNamesById = (id: number, users: any[]) => {
  const user = users.find((usr) => usr?.id === id);
  return user ? `${user.firstname} ${user.lastname}` : '';
};
export const dataTableDateFormatter = (cell: any) => {
  if (!cell) return '';
  let dateObj = cell;
  if (typeof cell !== 'object') {
    dateObj = new Date(cell);
  }
  return `${`0${dateObj.getUTCDate()}`.slice(-2)}/${`0${dateObj.getUTCMonth() + 1}`.slice(
    -2
  )}/${dateObj.getUTCFullYear()}`;
};
export const criticalPathApprovalFormatter = (cell: any) => {
  if (cell === 1 || cell === CriticalPathApprovals.APPROVE) return CriticalPathApprovals.APPROVE;
  if (cell === 0 || cell === CriticalPathApprovals.REJECT) return CriticalPathApprovals.REJECT;
  return CriticalPathApprovals.RA;
};
export const checkRequiredProperties = (
  object: {
    [key: string]: string | number | undefined;
  },
  requiredFields: string[]
): boolean => {
  const objectKeys = Object.keys(object);
  const isAllRequiredFieldsSet = requiredFields.every((requiredField: string) => objectKeys.includes(requiredField));
  for (const objectKey of objectKeys) {
    if (!isAllRequiredFieldsSet) return false;
    if (requiredFields.includes(objectKey) && !!object[objectKey] === false) {
      return false;
    }
  }
  return true;
};
export const resetInvalidDefaultValues = (
  payload: any,
  fieldDefaultValues: any,
  fieldsToReset: string[]
) => {
  const objectKeys = Object.keys(payload).filter(
    (prop) => fieldsToReset.includes(prop) && fieldDefaultValues[prop]
  );
  for (const key of objectKeys) {
    if (payload[key] === fieldDefaultValues[key]) {
      payload[key] = null;
    }
  }
};
export const showRequiredFields = (
  object: {
    [key: string]: any;
  },
  requiredFields: string[],
  fieldUnsetValues?: {
    [key: string]: any;
  }
) => {
  const requiredFieldsThatAreEmpty = [];
  for (const field of requiredFields) {
    // check to see if its null or undefined
    if (object[field] === null || object[field] === undefined) {
      requiredFieldsThatAreEmpty.push(field);
    } else {
      // if the field has a default value for the 'unset' state, treat the field as empty
      if (!!fieldUnsetValues && fieldUnsetValues[field] === object[field]) {
        requiredFieldsThatAreEmpty.push(field);
      }
      // if its an array check to ensure its not empty
      if (Array.isArray(object[field]) && !object[field].length) {
        requiredFieldsThatAreEmpty.push(field);
      }
      //  if its an object, check to ensure its not empty
      if (!Array.isArray(object[field]) && typeof object[field] === 'object') {
        const values = Object.entries(object[field]);
        if (!values.length) {
          requiredFieldsThatAreEmpty.push(field);
        }
      }
      // if its a string, check to ensure that it's not empty
      if (typeof object[field] === 'string') {
        if (!object[field].trim().length) {
          requiredFieldsThatAreEmpty.push(field);
        }
      }
    }
  }
  if (!requiredFieldsThatAreEmpty.length) {
    return requiredFieldsThatAreEmpty;
  }
  const stringValuesOfRequiredFieldsThatAreEmpty = requiredFieldsThatAreEmpty.map(
    (value) => {
      return value.replaceAll('_', ' ');
    }
  );
  return stringValuesOfRequiredFieldsThatAreEmpty;
};
export const checkRequiredPropertiesInList = (
  list: {
    [key: string]: string | number | undefined;
  }[],
  requiredFields: string[]
) => {
  const allRequired = list
    .map((listItem) => {
      return checkRequiredProperties(listItem, requiredFields);
    })
    .reduce((accumulator, status) => {
      return accumulator && status;
    }, true);
  return allRequired;
};
/**
 *  Loops over object keys to convert predefined properties from string to number
 *
 * @param  {{[key:string]:string|number|null}} payload
 * @returns { [key: string]: string | number | null }
 */
export const fieldsToNumber = (
  payload: {
    [key: string]: string | number | null;
  },
  fields: string[]
): { [key: string]: string | number | null } => {
  Object.keys(payload).forEach((field) => {
    const isNumberField = fields.includes(field);
    if (payload[field] !== null && isNumberField) payload[field] = Number(payload[field]);
  });
  return payload;
};
/**
 * A function that detects a valid URL using a regular expression
 *
 * @param value
 * @return {Boolean}
 */
export const urlRegex = (value: string) => /^https?:\/\//.test(value || '');
/**
 * A function that detects a valid Date in DD/MM/YYYY format using a regular expression
 *
 * @param value
 * @return {Boolean}
 */
export const dateRegex = (value: string) => /^(?:(?:31(\/|-|\.)(?:0?[13578]|1[02]))\1|(?:(?:29|30)(\/|-|\.)(?:0?[13-9]|1[0-2])\2))(?:(?:1[6-9]|[2-9]\d)?\d{2})$|^(?:29(\/|-|\.)0?2\3(?:(?:(?:1[6-9]|[2-9]\d)?(?:0[48]|[2468][048]|[13579][26])|(?:(?:16|[2468][048]|[3579][26])00))))$|^(?:0?[1-9]|1\d|2[0-8])(\/|-|\.)(?:(?:0?[1-9])|(?:1[0-2]))\4(?:(?:1[6-9]|[2-9]\d)?\d{2})$/.test(
  value || ''
);
/**
 * A function that utilizes a regex to check email strings
 *
 * @param {String} value
 * @returns {Boolean}
 */
export const emailIsValid = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value || '');
/**
 * A function to filter critical_path items maching a given list of statuses
 *
 * @param {Array} orderConfirmationData list
 * @param {Array} statusList Acceptable list of status
 * @returns {Array} List of the filtered criticalPath items
 */
export const filterCriticalPathItems = (
  orderConfirmationData: Record<string, any>,
  statusList: number[]
) => {
  return orderConfirmationData.filter((item: Record<string, any>) => statusList.includes(item.criticalPathStatus));
};
export const filterCriticalPathItemsByAuthorId = (
  orderConfirmationData: Record<string, any>,
  authorId: any
) => {
  return orderConfirmationData.filter(
    (item: Record<string, any>) => item.author_id === authorId
  );
};
/**
 *  A function to group critical path items by agreed_delivery_date
 *
 * @param  {Array} criticalPathDrops list
 * @returns {Array} List of the grouped criticalPath items
 */
export const groupCriticalPathItems = (criticalPathDrops: any, isDescending = false) => {
  let groupedCriticalPathDrops: any = [];
  let numGroups = 0;
  const groupByCollectionDate = (criticalPathDrop: any) => {
    const stringifiedGroupedCriticalPathDrops = groupedCriticalPathDrops.map(
      (groupedCriticalPathDrop: any) => JSON.stringify(groupedCriticalPathDrop)
    );
    const mondayOfWeek = dayjs(criticalPathDrop.collection_date)
      .startOf('week')
      .add(1, 'day');
    const collectionDate = formatDate(criticalPathDrop.collection_date);
    const controlRow = {
      id: collectionDate,
      mondayOfWeek: mondayOfWeek.toString(),
      agreed_delivery_date: collectionDate,
      control_row: `W/C ${formatDate(mondayOfWeek.toDate())}`,
      criticalPathStatus: CriticalPathStatus.ASSIGNED,
      brand: `W/C ${formatDate(mondayOfWeek.toDate())}`,
      brandName: `W/C ${formatDate(mondayOfWeek.toDate())}`,
    };
    if (!stringifiedGroupedCriticalPathDrops.includes(JSON.stringify(controlRow))) {
      numGroups++;
      groupedCriticalPathDrops.push(controlRow);
    }
    groupedCriticalPathDrops.push(criticalPathDrop);
  };

  const sortCPByDate = (a: any, b: any) => {
    const c = (new Date(a.collection_date) as unknown) as number;
    const d = (new Date(b.collection_date) as unknown) as number;
    return isDescending ? d - c : c - d;
  };
  criticalPathDrops.sort(sortCPByDate);
  criticalPathDrops.forEach(groupByCollectionDate);
  groupedCriticalPathDrops = groupByMonday(groupedCriticalPathDrops);
  return { groupedCriticalPathDrops, numGroups };
};
// Strips all duplicate 'control rows'
const groupByMonday = (groupedCriticalPathDrops: any) => {
  const data: any = [];
  const hashMap: any = {};
  for (const row of groupedCriticalPathDrops) {
    if (row.mondayOfWeek && hashMap[row.mondayOfWeek]) {
      // eslint-disable-next-line no-continue
      continue;
    }
    if (row.mondayOfWeek) {
      hashMap[row.mondayOfWeek] = row.mondayOfWeek;
    }
    data.push(row);
  }
  return data;
};
export const getTableLimit = (): number => Number(storage.getStorageItem(TABLE_LIMIT_KEY)) || 10;
export const valueNotSet = (value: any) => [undefined, null].includes(value);
export const noOp = () => {
};
export const getCriticalPathApprovalStatus = (status: number) => {
  let result;
  switch (status) {
    case 0:
      result = CriticalPathApprovals.REJECT;
      break;
    case 1:
      result = CriticalPathApprovals.APPROVE;
      break;
    default:
      result = CriticalPathApprovals.RA;
      break;
  }
  return result;
};
export enum OCFormOnSaveRedirect {
  dispatched,
  cp,
}
export const generateIds = (length: number) => {
  let result = '';
  const characters = '0123456789';
  const charactersLength = characters.length;
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
};
export const generateColorPickerClass = (rowIndex: number, columnIndex: number) => {
  return `color-picker-${rowIndex}-${columnIndex}`;
};
export const isControlRow = (row: any) => !!row.control_row;
